import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Search, Ban, Copy, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import PageHeader, { AccountSelector, EmptyState } from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';

export default function BannedWords() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [words, setWords] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.get('/accounts').then(({ data }) => {
      setAccounts(data);
      if (data[0]) setAccount(prev => prev || data[0].id);
    }).catch(e => toast.error(apiError(e, 'Failed to load accounts')));
  }, []);

  const load = async (acc) => {
    if (!acc) return;
    try {
      const { data } = await api.get('/banned-words', { params: acc === 'all' ? {} : { account_id: acc } });
      setWords(data);
    } catch (e) { toast.error(apiError(e, 'Failed to load banned words')); }
  };
  useEffect(() => { load(account); }, [account]);

  const remove = async (id) => {
    try {
      await api.delete(`/banned-words/${id}`);
      setWords(words.filter(x => x.id !== id));
      toast.success('Removed');
    } catch (e) { toast.error(apiError(e, 'La suppression a échoué')); }
  };

  const filtered = words.filter(w => w.word.toLowerCase().includes(q.toLowerCase()));

  const copyToOtherAccounts = async () => {
    const others = accounts.filter(a => a.id !== account);
    if (!others.length) { toast.info('Aucun autre compte vers lequel copier'); return; }
    if (!words.length) { toast.info('Aucun mot banni à copier'); return; }
    if (!window.confirm(`Copy these ${words.length} banned words to your ${others.length} other account(s)?`)) return;
    let added = 0;
    for (const a of others) {
      for (const w of words) {
        try { await api.post('/banned-words', { account_id: a.id, word: w.word.toLowerCase() }); added++; } catch { /* likely already exists */ }
      }
    }
    toast.success(`Copied to ${others.length} account(s) (${added} entries)`);
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in" data-testid="banned-words-page">
      <PageHeader
        title="Banned words"
        subtitle={`${words.length} banned words`}
        actions={<>
          <AccountSelector value={account} onChange={setAccount} accounts={accounts} />
          <NewWord account={account} onCreated={() => load(account)} />
        </>}
        onRefresh={() => load(account)}
      />

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="banned-words-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search banned words..." className="bg-white/5 border-white/10 text-white pl-9 h-9" />
        </div>
        <Button data-testid="copy-banned-to-accounts" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10" onClick={copyToOtherAccounts}><Copy size={14} className="mr-1.5" /> Copy to other accounts</Button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        {filtered.length === 0 ? (
          <EmptyState icon={Ban} title="No banned words" desc="Add words or phrases here. If an employee tries to send a message containing them, the message will be blocked." />
        ) : (
          <div className="flex flex-wrap gap-2">
            {filtered.map(w => (
              <Badge key={w.id} variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-300 py-1.5 px-3 text-sm" data-testid={`banned-word-${w.word.replace(/\s+/g, '-')}`}>
                <Ban size={12} className="mr-1.5" /> {w.word}
                <button data-testid={`remove-banned-word-${w.id}`} onClick={() => remove(w.id)} className="ml-2 hover:text-rose-100"><X size={12} /></button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewWord({ account, onCreated }) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!word) { toast.error('Required'); return; }
    if (!account || account === 'all') { toast.error('Select an account first'); return; }
    setSaving(true);
    try {
      await api.post('/banned-words', { account_id: account, word: word.toLowerCase() });
      setOpen(false); setWord('');
      toast.success('Added');
      onCreated();
    } catch (e) { toast.error(apiError(e, 'L’ajout a échoué')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-banned-word-button" className="bg-indigo-500 hover:bg-indigo-600"><Plus size={16} className="mr-1.5" /> Add banned words</Button></DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Add banned word</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Word or phrase</Label><Input data-testid="banned-word-input" value={word} onChange={e => setWord(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="e.g. snapchat" /></div>
          <div className="text-xs text-slate-500">Tip: use lowercase. Detection is case-insensitive and matches whole words.</div>
          <Button data-testid="banned-word-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Adding...' : 'Add'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
